import { Sun, Moon, Type, BookOpen, Download, Coffee, Library, SquarePen, Users } from 'lucide-react';
import { useAppStore } from '@/lib/store';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface EditorToolbarProps {
    isDark: boolean;
    setTheme: (theme: 'light' | 'dark' | 'system') => void;
    editorMode: 'standard' | 'typewriter' | 'book';
    setEditorMode: (mode: 'standard' | 'typewriter' | 'book') => void;
    wordCount: number;
    themeColors: any;
    isMobile?: boolean;
    onExport?: () => void;
    onPublish?: () => void;
    onBetaReaders?: () => void;
}

export function EditorToolbar({
    isDark,
    setTheme,
    editorMode,
    setEditorMode,
    wordCount,
    themeColors,
    isMobile,
    onExport,
    onPublish,
    onBetaReaders,
}: EditorToolbarProps) {
    const { themeProfile } = useAppStore();
    const location = useLocation();
    const isPaperwhite = themeProfile.preset === 'paperwhite';

    const buttonStyle = {
        color: themeColors.textMuted,
        backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(90, 122, 122, 0.1)'
    };

    const activeButtonStyle = {
        color: 'white',
        backgroundColor: themeColors.sage
    };

    if (isMobile) {
        return (
            <div
                className="flex flex-col border-b"
                style={{
                    backgroundColor: themeColors.bgSecondary,
                    borderColor: themeColors.border
                }}
            >
                <div className="flex items-center justify-between px-3 py-2">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setTheme(isPaperwhite ? 'dark' : 'light')}
                            className="p-2 rounded-lg active:scale-95 transition-transform"
                            style={buttonStyle}
                        >
                            {isPaperwhite ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                        </button>
                        <span className="text-xs" style={{ color: themeColors.textMuted }}>
                            {wordCount.toLocaleString()} words
                        </span>
                    </div>

                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setEditorMode(editorMode === 'typewriter' ? 'standard' : 'typewriter')}
                            className="p-2 rounded-lg active:scale-95 transition-all"
                            style={editorMode === 'typewriter' ? activeButtonStyle : buttonStyle}
                        >
                            <Type className="w-4 h-4" />
                        </button>

                        <button
                            onClick={() => setEditorMode(editorMode === 'book' ? 'standard' : 'book')}
                            className="p-2 rounded-lg active:scale-95 transition-all"
                            style={editorMode === 'book' ? activeButtonStyle : buttonStyle}
                        >
                            <BookOpen className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Site Navigation Group (Mobile) */}
                <div className="flex items-center justify-center p-2 border-t" style={{ borderColor: themeColors.border + '40' }}>
                    <div className="flex items-center p-1 rounded-full border shadow-sm"
                        style={{
                            backgroundColor: 'rgba(0,0,0,0.03)',
                            borderColor: themeColors.border + '50'
                        }}>
                        <Link
                            to="/read"
                            className={cn(
                                "flex-1 px-4 py-1.5 rounded-full text-[10px] font-bold transition-all flex items-center gap-1.5",
                                location.pathname === '/read' ? "shadow-md" : ""
                            )}
                            style={{
                                backgroundColor: location.pathname === '/read' ? themeColors.sage : 'transparent',
                                color: location.pathname === '/read' ? 'white' : themeColors.textMuted
                            }}
                        >
                            <Coffee className="w-3 h-3" />
                            <span>READ</span>
                        </Link>
                        <Link
                            to="/library"
                            className={cn(
                                "flex-1 px-4 py-1.5 rounded-full text-[10px] font-bold transition-all flex items-center gap-1.5",
                                location.pathname === '/library' ? "shadow-md" : ""
                            )}
                            style={{
                                backgroundColor: location.pathname === '/library' ? themeColors.sage : 'transparent',
                                color: location.pathname === '/library' ? 'white' : themeColors.textMuted
                            }}
                        >
                            <Library className="w-3 h-3" />
                            <span>LIBRARY</span>
                        </Link>
                        <Link
                            to="/study"
                            className={cn(
                                "flex-1 px-4 py-1.5 rounded-full text-[10px] font-bold transition-all flex items-center gap-1.5",
                                location.pathname.startsWith('/study') ? "shadow-md" : ""
                            )}
                            style={{
                                backgroundColor: location.pathname.startsWith('/study') ? themeColors.sage : 'transparent',
                                color: location.pathname.startsWith('/study') ? 'white' : themeColors.textMuted
                            }}
                        >
                            <SquarePen className="w-3 h-3" />
                            <span>STUDIO</span>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center gap-4">
            {/* Site Navigation Group - Mini Version of FloatingNav */}
            <div className="flex items-center p-1 rounded-full border transition-all shadow-sm"
                style={{
                    backgroundColor: 'rgba(0,0,0,0.03)',
                    borderColor: themeColors.border + '50'
                }}>
                <Link
                    to="/read"
                    className={cn(
                        "p-1.5 rounded-full transition-all hover:scale-110 active:scale-95",
                        location.pathname === '/read' ? "shadow-md" : "hover:bg-current/10"
                    )}
                    style={{
                        backgroundColor: location.pathname === '/read' ? themeColors.sage : 'transparent',
                        color: location.pathname === '/read' ? 'white' : themeColors.textMuted
                    }}
                    title="Read & Feed"
                >
                    <Coffee className="w-4 h-4" />
                </Link>
                <Link
                    to="/library"
                    className={cn(
                        "p-1.5 rounded-full transition-all hover:scale-110 active:scale-95",
                        location.pathname === '/library' ? "shadow-md" : "hover:bg-current/10"
                    )}
                    style={{
                        backgroundColor: location.pathname === '/library' ? themeColors.sage : 'transparent',
                        color: location.pathname === '/library' ? 'white' : themeColors.textMuted
                    }}
                    title="My Library"
                >
                    <Library className="w-4 h-4" />
                </Link>
                <Link
                    to="/study"
                    className={cn(
                        "p-1.5 rounded-full transition-all hover:scale-110 active:scale-95",
                        location.pathname.startsWith('/study') ? "shadow-md" : "hover:bg-current/10"
                    )}
                    style={{
                        backgroundColor: location.pathname.startsWith('/study') ? themeColors.sage : 'transparent',
                        color: location.pathname.startsWith('/study') ? 'white' : themeColors.textMuted
                    }}
                    title="The Studio"
                >
                    <SquarePen className="w-4 h-4" />
                </Link>
            </div>

            <div className="w-px h-5 md:block hidden" style={{ backgroundColor: themeColors.border + '40' }} />

            <div className="flex items-center gap-1">
                <button
                    onClick={() => setTheme(isPaperwhite ? 'dark' : 'light')}
                    className="p-2 rounded-lg transition-colors hover:bg-current/10"
                    style={{ color: themeColors.textMuted }}
                    title="Toggle paperwhite mode"
                >
                    {isPaperwhite ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </button>

                <button
                    onClick={() => setEditorMode(editorMode === 'typewriter' ? 'standard' : 'typewriter')}
                    className="p-2 rounded-lg transition-colors hover:bg-current/10"
                    style={{ color: editorMode === 'typewriter' ? themeColors.sage : themeColors.textMuted }}
                    title="Typewriter mode"
                >
                    <Type className="w-4 h-4" />
                </button>

                <button
                    onClick={() => setEditorMode(editorMode === 'book' ? 'standard' : 'book')}
                    className="p-2 rounded-lg transition-colors hover:bg-current/10"
                    style={{ color: editorMode === 'book' ? themeColors.sage : themeColors.textMuted }}
                    title="Book mode"
                >
                    <BookOpen className="w-4 h-4" />
                </button>

                {onPublish && (
                    <button
                        onClick={onPublish}
                        className="p-2 rounded-lg transition-colors hover:bg-current/10"
                        style={{ color: themeColors.textMuted }}
                        title="Publish to The Coffee House"
                    >
                        <Coffee className="w-4 h-4" />
                    </button>
                )}

                {onExport && (
                    <button
                        onClick={onExport}
                        className="p-2 rounded-lg transition-colors hover:bg-current/10"
                        style={{ color: themeColors.textMuted }}
                        title="Export manuscript…"
                    >
                        <Download className="w-4 h-4" />
                    </button>
                )}

                {onBetaReaders && (
                    <button
                        onClick={onBetaReaders}
                        className="p-2 rounded-lg transition-colors hover:bg-current/10"
                        style={{ color: themeColors.textMuted }}
                        title="Beta Readers"
                    >
                        <Users className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
}
